import { useMemo, useState } from 'react'
import clsx from 'clsx'
import { AxiosError } from 'axios'
import { useQuery } from 'react-query'
import { AlertCircle, BookOpen, CheckCircle2, RefreshCw, Target, TrendingUp } from 'lucide-react'
import { SkillGrid } from '../SkillGrid'
import { useSkillmapGrid } from '../../hooks/useSkillmapGrid'
import { api } from '../../lib/api'
import {
  STATUS_BADGE_STYLES,
  STATUS_DESCRIPTIONS,
  STATUS_LABELS,
  STATUS_RING_COLORS
} from '../../lib/skillmap/constants'
import { formatDateTime, formatNumber, formatScore } from '../../lib/skillmap/formatters'
import { CompetencyEntry, CompetencyStatus, CurriculumSummary, SkillmapData } from '../../lib/skillmap/types'

type Props = {
  sophomorixUnid: string
}

type CompetencyHistoryEntry = {
  competency_name?: string
  changed_by_username?: string
  alter_datum?: string
  status_alt?: string
  status_neu?: string
  alter_wert?: string
  neuer_wert?: string
}

type CompetencyReportEntry = {
  curriculum_id: number
  curriculum_name: string | null
  subject: string | null
  total_competencies: number
  not_attempted: number
  in_progress: number
  not_competent: number
  competent: number
  proficient: number
  avg_score: number | null
}

const fetchSkillmap = async (unid: string): Promise<SkillmapData> => {
  const response = await api.get(`/students/${unid}/skillmap`)
  const { data } = response.data || {}
  if (!data) {
    throw new Error('Skillmap-Daten konnten nicht geladen werden.')
  }
  return data
}

const fetchHistory = async (userId: number, competencyId: number, studentUnid: string): Promise<CompetencyHistoryEntry[]> => {
  const response = await api.get(`/user-competencies/user/${userId}/history`, {
    params: { competencyId, limit: 20, studentUnid }
  })
  return response.data?.data || []
}

const fetchReport = async (userId: number): Promise<CompetencyReportEntry[]> => {
  const response = await api.get(`/user-competencies/user/${userId}/report`)
  return response.data?.data || []
}

export default function StudentKompetenzenPanel({ sophomorixUnid }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [filterStatus, setFilterStatus] = useState<CompetencyStatus | 'all'>('all')
  const [collapsedCurricula, setCollapsedCurricula] = useState<Set<number>>(new Set())

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching
  } = useQuery<SkillmapData, AxiosError<{ message?: string }>>(
    ['students-v2-skillmap', sophomorixUnid],
    () => fetchSkillmap(sophomorixUnid),
    { enabled: Boolean(sophomorixUnid), staleTime: 60 * 1000 }
  )

  const selectedCompetency = useMemo(() => {
    if (!data || !selectedId) return null
    const competencyId = selectedId.replace('competency-', '')
    return data.competencies.find((c) => c.id.toString() === competencyId) || null
  }, [data, selectedId])

  const {
    data: historyData,
    isLoading: isLoadingHistory
  } = useQuery<CompetencyHistoryEntry[]>(
    ['students-v2-skillmap-history', data?.userId, selectedCompetency?.id, sophomorixUnid],
    () => fetchHistory(Number(data?.userId), Number(selectedCompetency?.id), sophomorixUnid),
    {
      enabled: Boolean(data?.userId && selectedCompetency?.id),
      staleTime: 30 * 1000
    }
  )

  const { data: reportData } = useQuery<CompetencyReportEntry[]>(
    ['students-v2-skillmap-report', data?.userId],
    () => fetchReport(Number(data?.userId)),
    {
      enabled: Boolean(data?.userId),
      staleTime: 60 * 1000
    }
  )

  const { gridItems } = useSkillmapGrid({ data, filterStatus, collapsedCurricula })

  const curriculumById = useMemo(() => {
    if (!data) return new Map<number, CurriculumSummary>()
    return new Map(data.curricula.map((curriculum) => [curriculum.curriculumId, curriculum] as const))
  }, [data])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="flex flex-col items-center space-y-4">
          <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Kompetenzen werden geladen ...</p>
        </div>
      </div>
    )
  }

  if (isError) {
    const axiosError = error as AxiosError<{ message?: string }>
    const message = axiosError?.response?.data?.message || error?.message || 'Kompetenzen konnten nicht geladen werden.'
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900/40 dark:bg-red-900/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-700 dark:text-red-300">{message}</p>
            <button
              onClick={() => refetch()}
              className="mt-3 inline-flex items-center rounded bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!data) return null

  const summary = data.summary
  const selectedStatus = selectedCompetency?.status || 'not_attempted'
  const curriculumRows = data.curricula.length > 0
    ? data.curricula
    : (reportData || []).map((row) => ({
        curriculumId: row.curriculum_id,
        name: row.curriculum_name,
        subject: row.subject,
        label: row.subject || row.curriculum_name || 'Curriculum',
        color: '#94a3b8',
        totalCompetencies: row.total_competencies,
        trackedCompetencies: row.total_competencies - row.not_attempted,
        statusCounts: {
          proficient: row.proficient,
          competent: row.competent,
          in_progress: row.in_progress,
          not_yet_competent: row.not_competent,
          not_attempted: row.not_attempted
        },
        mastered: row.competent + row.proficient,
        proficient: row.proficient,
        competent: row.competent,
        inProgress: row.in_progress,
        notYetCompetent: row.not_competent,
        notAttempted: row.not_attempted,
        averageScore: row.avg_score,
        highlightRatio: 0,
        lastUpdated: null
      }))

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Kompetenzen</h3>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="inline-flex items-center rounded-md bg-primary-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-60"
        >
          <RefreshCw className={`mr-2 h-3 w-3 ${isFetching ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><BookOpen className="h-4 w-4" />Kompetenzen gesamt</div>
          <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(summary.totalCompetencies)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><CheckCircle2 className="h-4 w-4" />Beherrscht</div>
          <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(summary.mastered)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><Target className="h-4 w-4" />In Arbeit</div>
          <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatNumber(summary.inProgress)}</div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"><TrendingUp className="h-4 w-4" />Ø Score</div>
          <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">{formatScore(summary.averageScore)}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as CompetencyStatus | 'all')}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
        >
          <option value="all">Alle anzeigen</option>
          <option value="in_progress">In Arbeit</option>
          <option value="competent">Kompetent</option>
          <option value="proficient">Proficient</option>
          <option value="not_yet_competent">Noch nicht sicher</option>
          <option value="not_attempted">Nicht gestartet</option>
        </select>
        <div className="flex flex-wrap gap-2">
          {data.curricula.map((curriculum) => {
            const isCollapsed = collapsedCurricula.has(curriculum.curriculumId)
            return (
              <button
                key={curriculum.curriculumId}
                onClick={() => {
                  const next = new Set(collapsedCurricula)
                  if (isCollapsed) next.delete(curriculum.curriculumId)
                  else next.add(curriculum.curriculumId)
                  setCollapsedCurricula(next)
                }}
                className={clsx(
                  'inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium',
                  isCollapsed
                    ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                    : 'bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300'
                )}
              >
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: curriculum.color }} />
                {curriculum.label || curriculum.subject || curriculum.name || 'Curriculum'}
              </button>
            )
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[2fr_1fr]">
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          <SkillGrid items={gridItems} onTileClick={(item) => setSelectedId(item.id)} />
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
          {selectedCompetency ? (
            <div className="space-y-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Technischer Name</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{selectedCompetency.name}</p>
                {selectedCompetency.childFriendlyName && (
                  <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">Schülername: {selectedCompetency.childFriendlyName}</p>
                )}
              </div>
              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_BADGE_STYLES[selectedStatus]}`}>
                {STATUS_LABELS[selectedStatus]}
              </span>
              <p className="text-xs text-gray-600 dark:text-gray-300">{STATUS_DESCRIPTIONS[selectedStatus]}</p>
              <div>
                <div className="mb-1 flex justify-between text-xs text-gray-600 dark:text-gray-300">
                  <span>Fortschritt</span>
                  <span>{formatScore(selectedCompetency.masteryScore, 0)}</span>
                </div>
                <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
                  <div
                    className="h-2 rounded-full"
                    style={{
                      width: `${Math.min(100, Math.max(0, selectedCompetency.progress * 100))}%`,
                      backgroundColor: STATUS_RING_COLORS[selectedStatus]
                    }}
                  />
                </div>
              </div>
              <div className="text-xs text-gray-600 dark:text-gray-300">
                <div>Fach: {curriculumById.get(selectedCompetency.curriculumId)?.label || '–'}</div>
                <div>Level: {selectedCompetency.levelTitle || selectedCompetency.levelAchieved || '–'}</div>
                <div>Versuche: {selectedCompetency.attemptCount}</div>
                <div>Evidenzen: {selectedCompetency.evidenceCount}</div>
                <div>Aktualisiert: {formatDateTime(selectedCompetency.lastUpdated)}</div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200">Kurs-Breakdown</h4>
                <div className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                  {selectedCompetency.courseBreakdown.length > 0 ? selectedCompetency.courseBreakdown.map((entry) => (
                    <div key={entry.userCompetencyId} className="rounded border border-gray-200 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{entry.courseName || 'Allgemein'}</div>
                      <div>Status: {STATUS_LABELS[entry.status]}</div>
                      <div>Score: {formatScore(entry.masteryScore)}</div>
                    </div>
                  )) : <div>Keine Kursdaten vorhanden.</div>}
                </div>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-200">Verlauf</h4>
                <div className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                  {isLoadingHistory && <div>Lade Verlauf ...</div>}
                  {!isLoadingHistory && (historyData || []).length === 0 && <div>Kein Verlauf vorhanden.</div>}
                  {(historyData || []).map((entry, idx) => (
                    <div key={`${entry.alter_datum || 'row'}-${idx}`} className="rounded border border-gray-200 p-2 dark:border-gray-700 dark:bg-gray-800/60">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{formatDateTime(entry.alter_datum || null)}</div>
                      <div>Von: {entry.status_alt || entry.alter_wert || '–'}</div>
                      <div>Nach: {entry.status_neu || entry.neuer_wert || '–'}</div>
                      <div>Geändert von: {entry.changed_by_username || 'System'}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">Kachel auswählen, um Details zu sehen.</p>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-900">
        <h4 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Curriculum-Auswertung</h4>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="px-2 py-2">Fach/Curriculum</th>
                <th className="px-2 py-2">Gesamt</th>
                <th className="px-2 py-2">Kompetent+Proficient</th>
                <th className="px-2 py-2">In Arbeit</th>
                <th className="px-2 py-2">Noch nicht sicher</th>
                <th className="px-2 py-2">Nicht gestartet</th>
                <th className="px-2 py-2">Ø Score</th>
                <th className="px-2 py-2">Letzte Aktualisierung</th>
              </tr>
            </thead>
            <tbody>
              {curriculumRows.map((row) => (
                <tr key={row.curriculumId} className="border-b border-gray-100 text-gray-700 dark:border-gray-800 dark:text-gray-300">
                  <td className="px-2 py-2">{row.label || row.subject || row.name || 'Curriculum'}</td>
                  <td className="px-2 py-2">{row.totalCompetencies}</td>
                  <td className="px-2 py-2">{row.mastered}</td>
                  <td className="px-2 py-2">{row.inProgress}</td>
                  <td className="px-2 py-2">{row.notYetCompetent}</td>
                  <td className="px-2 py-2">{row.notAttempted}</td>
                  <td className="px-2 py-2">{formatScore(row.averageScore)}</td>
                  <td className="px-2 py-2">{formatDateTime(row.lastUpdated)}</td>
                </tr>
              ))}
              {curriculumRows.length === 0 && (
                <tr>
                  <td className="px-2 py-4 text-gray-500 dark:text-gray-400" colSpan={8}>Keine Curriculum-Auswertungen verfügbar.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
